/* eslint-disable react/jsx-pascal-case */
import React, { PropsWithChildren } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ErrorPrompt } from '@opendoor/bricks/complex/ErrorPrompt';
import { InputProps } from '@opendoor/bricks/core/Input';
import { InputType } from '@opendoor/bricks/system/input.types';
import {
  colors,
  fontSizesNamed,
  fontSizesNamedWithUnits,
  fontWeights,
  radii,
  space,
} from '@opendoor/bricks/theme/eero';
import { isEmpty } from 'lodash';

type InputLabelSize = 'small' | 'medium' | 'large';

const ASSERT_NOT_NULL_ERROR = 'This field cannot be empty.';

interface IBasicTextInput
  extends Pick<
    InputProps,
    | 'name'
    | 'autoFocus'
    | 'className'
    | 'disabled'
    | 'onBlur'
    | 'onKeyDown'
    | 'onFocus'
    | 'required'
    | 'role'
    | 'autoComplete'
  > {
  /**
   * Callback handler when the current value of the input has changed
   */
  onChange: (name: string, value?: string | number, validFormat?: boolean) => void;
  // Optional:

  id?: string;
  /**
   * Accessibility label to use, in scenarios where the input has no visible label. A11y: It's usefult for screen readers
   */
  ariaLabel?: string;

  /**
   * Accessibility label to indicate the item it is labelled by. A11y: It's useful for screen readers
   */
  ariaLabelledby?: string;

  /**
   * Input corner style variations
   */
  designStyle?: 'rounded' | 'squared' | 'rounded-square';

  /**
   * Text alignment of input value
   */
  alignText?: 'left' | 'center' | 'right';

  /**
   * Whether or not the input should assert null values
   */
  assertNotNull?: boolean;

  /**
   * Current value of the input
   */
  content?: string | number;

  /**
   * Message displayed when the input has an error
   */
  errorMessage?: string;

  /**
   * Whether or not the input has an error
   */
  hasError?: boolean;

  /**
   * Sets the input's `name` attribute used to identify the input when submitting the form (Duplicate of name prop)
   */
  inputName?: string;

  /**
   * Input label size
   */
  inputLabelSize?: InputLabelSize;

  /**
   * Contains a hint to the user of what can be entered in the input
   */
  placeholder?: string;

  /**
   * Label title
   */
  title?: string;

  /**
   * Label subtitle
   */
  subtitle?: string;

  /**
   * Sets the input width to 100% when true
   */
  unboundedWidth?: boolean;

  /**
   * Adjusts styling when the input has a button appended to it
   */
  isCtaInput?: boolean;
}

export interface UNSAFE_ISingleLineInputProps extends Omit<IBasicTextInput, 'onChange'> {
  inputType?: InputType;
  onChange: (name?: string, value?: string) => void;
}

const UNSAFE_SingleLineInputWrapper = styled.div<
  Pick<UNSAFE_ISingleLineInputProps, 'unboundedWidth'>
>`
  width: ${(props) => (props.unboundedWidth ? '100%' : '350px')};
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const UNSAFE_LabelTitle = styled.div<
  Pick<UNSAFE_ISingleLineInputProps, 'inputLabelSize'> & { disabled: boolean }
>`
  font-weight: ${fontWeights.bold};
  font-size: ${(props) => {
    if (props.inputLabelSize === 'large') {
      return fontSizesNamed.s2;
    } else if (props.inputLabelSize === 'medium') {
      return fontSizesNamedWithUnits.s1;
    }

    return fontSizesNamedWithUnits.s0;
  }};
  text-align: left;
  color: ${(props) => (props.disabled ? colors.neutrals70 : colors.neutrals100)};
  margin-bottom: ${space[3]}px;
`;

const UNSAFE_LabelSubtitle = styled.div`
  margin-bottom: ${space[3]}px;
  font-weight: normal;
  font-size: ${fontSizesNamedWithUnits.s0};
  text-align: left;
  color: ${colors.neutrals90};
`;

const UNSAFE_InvalidStyle = css`
  border: 2px solid ${colors.red50};
`;

const UNSAFE_SingleInput = styled.input<
  Pick<UNSAFE_ISingleLineInputProps, 'alignText' | 'designStyle' | 'isCtaInput'> & {
    isInvalid: boolean;
  }
>`
  width: 100%;
  height: 56px;
  font-size: ${fontSizesNamedWithUnits.s1};
  text-align: ${(props) => props.alignText};
  border: 1px solid ${colors.neutrals40};
  box-sizing: border-box;
  padding: 10px 12px;
  ${(props) => props.isInvalid && !props.isCtaInput && UNSAFE_InvalidStyle};
  border-radius: ${(props) => {
    if (props.designStyle === 'rounded') {
      return radii.rounded;
    } else if (props.designStyle === 'squared') {
      return radii.squared;
    } else if (props.designStyle === 'rounded-square') {
      return radii.roundedSquare;
    }
  }};

  &:disabled {
    background: ${colors.neutrals10};
    cursor: not-allowed;
  }
`;

/**
 * @deprecated Use core/Input instead
 */
export class UNSAFE_SingleLineInput extends React.PureComponent<
  UNSAFE_ISingleLineInputProps & PropsWithChildren
> {
  public static defaultProps: Partial<UNSAFE_ISingleLineInputProps> = {
    alignText: 'left',
    designStyle: 'rounded-square',
    inputLabelSize: 'small',
    inputType: 'text',
    unboundedWidth: false,
  };

  public render(): JSX.Element {
    const {
      id,
      alignText,
      ariaLabel,
      ariaLabelledby,
      assertNotNull,
      autoComplete,
      autoFocus,
      className,
      content,
      designStyle,
      disabled,
      hasError,
      inputLabelSize,
      inputType,
      inputName,
      name,
      onBlur,
      onFocus,
      onKeyDown,
      placeholder,
      required,
      title,
      subtitle,
      unboundedWidth,
      isCtaInput,
    } = this.props;

    const errorMessage =
      assertNotNull && isEmpty(content) ? ASSERT_NOT_NULL_ERROR : this.props.errorMessage;

    return (
      <UNSAFE_SingleLineInputWrapper unboundedWidth={unboundedWidth}>
        {title && (
          <UNSAFE_LabelTitle inputLabelSize={inputLabelSize} disabled={!!disabled}>
            {title}
          </UNSAFE_LabelTitle>
        )}
        {subtitle && <UNSAFE_LabelSubtitle>{subtitle}</UNSAFE_LabelSubtitle>}
        <UNSAFE_SingleInput
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          className={className}
          autoComplete={autoComplete}
          designStyle={designStyle}
          alignText={alignText}
          isInvalid={!!hasError || (!!assertNotNull && isEmpty(content))}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          disabled={disabled}
          name={inputName || name}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onChange={this.handleChange}
          placeholder={placeholder}
          required={required}
          type={inputType}
          value={content || ''}
          isCtaInput={isCtaInput}
        />
        {errorMessage && <ErrorPrompt message={errorMessage} />}
        {this.props.children}
      </UNSAFE_SingleLineInputWrapper>
    );
  }

  private handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    this.props.onChange(this.props.name, e.currentTarget.value || undefined);
  };
}
